import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loader2, MessageSquare } from 'lucide-react'
import { startPortalSession, getPortalQueues, type PortalQueue } from '@/lib/portal-api'
import { toast } from 'sonner'

function PortalEntry() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  console.log('PortalEntry component mounted')

  const [loading, setLoading] = useState(false)
  const [queuesLoading, setQueuesLoading] = useState(false)
  const [queues, setQueues] = useState<PortalQueue[]>([])
  const [companyName, setCompanyName] = useState<string>('')
  const [appName, setAppName] = useState<string>('')
  
  // Get app_id from URL params
  const appId = searchParams.get('app_id')
  const queueIdParam = searchParams.get('queue_id')
  const newConversation = searchParams.get('new') === 'true'
  
  // Store app_id immediately if present
  useEffect(() => {
    if (appId) {
      localStorage.setItem('portal_app_id', appId)
      sessionStorage.setItem('portal_app_id', appId)
    }
  }, [appId])

  const [formData, setFormData] = useState({
    name: localStorage.getItem('portal_name') || '',
    email: localStorage.getItem('portal_email') || '',
    phone: '',
    queueId: queueIdParam || '',
    message: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    // If this is a new conversation request, clear any existing session
    if (newConversation) {
      localStorage.removeItem('portal_token')
      localStorage.removeItem('conversation_id')
    }
    
    // Check for existing session in localStorage
    const existingToken = localStorage.getItem('portal_token')
    const existingAppId = localStorage.getItem('portal_app_id')
    
    // If no app_id in URL but we have one in storage, redirect with app_id
    if (!appId && existingAppId) {
      navigate(`/portal?app_id=${existingAppId}`)
      return
    }
    
    // If we have an existing token and not starting a new conversation, try to use it
    if (existingToken && !newConversation) {
      navigate(`/portal/chat?token=${existingToken}`)
      return
    }
    
    if (!appId) {
      toast.error('This support link is invalid. Please check the URL and try again.')
      return
    }

    // If no queue ID provided, load available queues
    if (!queueIdParam) {
      loadQueues()
    }
  }, [appId, queueIdParam, navigate, newConversation])

  const loadQueues = async () => {
    if (!appId) return

    setQueuesLoading(true)
    try {
      const data = await getPortalQueues(appId)
      setQueues(data.queues)
      setCompanyName(data.company_name)
      setAppName(data.app_name)
    } catch (error) {
      console.error('Failed to load queues:', error)
      toast.error('Failed to load available support queues. Please try again.')
    } finally {
      setQueuesLoading(false)
    }
  }

  const validateForm = () => {
    console.log('validateForm called with formData:', formData)
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please describe how we can help you'
    }

    if (!queueIdParam && !formData.queueId && queues.length > 0) {
      newErrors.queueId = 'Please select what area you need help with'
    }

    console.log('Validation errors:', newErrors)
    console.log('Queues:', queues, 'queueIdParam:', queueIdParam)
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('handleSubmit called')
    e.preventDefault()
    console.log('Form submitted', { formData, appId, queueIdParam })

    const isValid = validateForm()
    console.log('Validation result:', isValid, 'appId:', appId)
    if (!isValid || !appId) {
      console.log('Validation failed or no appId')
      return
    }

    setLoading(true)
    try {
      console.log('Starting portal session with:', {
        appId,
        customerInfo: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined
        },
        initialMessage: formData.message.trim(),
        queueId: formData.queueId || queueIdParam || undefined
      })

      const response = await startPortalSession(
        appId,
        {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim() || undefined
        },
        formData.message.trim(),
        formData.queueId || queueIdParam || undefined
      )

      console.log('Portal session started:', response)

      // Store portal token and app_id in localStorage
      localStorage.setItem('portal_token', response.portal_token)
      localStorage.setItem('conversation_id', response.conversation_id)
      localStorage.setItem('portal_app_id', appId)
      // Also in sessionStorage for backward compatibility
      sessionStorage.setItem('portal_app_id', appId)
      
      // Store user info in localStorage for future use
      localStorage.setItem('portal_name', formData.name.trim())
      localStorage.setItem('portal_email', formData.email.trim())

      // Navigate to chat view
      navigate(`/portal/chat?token=${response.portal_token}`)
    } catch (error) {
      console.error('Failed to start portal session:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to start support session. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!appId) {
    return (
      <div className="h-[100dvh] flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Invalid Support Link</CardTitle>
            <CardDescription>
              This support link appears to be invalid. Please check the URL and try again.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="h-[100dvh] flex items-center justify-center bg-gray-50 p-4 overflow-auto">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-primary" />
            <CardTitle>Contact Support</CardTitle>
          </div>
          {companyName && appName && (
            <div className="text-sm text-muted-foreground mt-2">
              {companyName} • {appName}
            </div>
          )}
          <CardDescription>
            We're here to help! Please fill out the form below and we'll get back to you as soon as possible.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Your name"
                className={errors.name ? 'border-red-500' : ''}
              />
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="your.email@example.com"
                className={errors.email ? 'border-red-500' : ''}
              />
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone (optional)</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 (555) 000-0000"
              />
            </div>

            {!queueIdParam && queues.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="queue">What area do you need help? *</Label>
                <Select
                  value={formData.queueId}
                  onValueChange={(value) => setFormData({ ...formData, queueId: value })}
                >
                  <SelectTrigger className={errors.queueId ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {queues.map((queue) => (
                      <SelectItem key={queue.queue_id} value={queue.queue_id}>
                        {queue.queue_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.queueId && (
                  <p className="text-sm text-red-500">{errors.queueId}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="message">How can we help you? *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Please describe your issue or question..."
                rows={4}
                className={errors.message ? 'border-red-500' : ''}
              />
              {errors.message && (
                <p className="text-sm text-red-500">{errors.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || queuesLoading}
              className="w-full"
              onClick={() => console.log('Button clicked')}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Starting conversation...
                </>
              ) : (
                'Start Conversation'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

export { PortalEntry }